import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/services/api";
import { Minus, Plus, Loader2, ShoppingCart, AlertTriangle } from "lucide-react";

const fmt = (n) => `$${(n || 0).toLocaleString("es-CO")}`;

export default function MayoristaComprar() {
  const navigate = useNavigate();
  const [catalogo, setCatalogo] = useState(null);
  const [cantidades, setCantidades] = useState({});
  const [notas, setNotas] = useState("");
  const [fechaEntrega, setFechaEntrega] = useState("");
  const [requiereFactura, setRequiereFactura] = useState(false);
  const [fact, setFact] = useState({});
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api
      .get("/mayoristas/me/catalogo")
      .then((res) => setCatalogo(res.data))
      .catch(() => toast.error("Error cargando el catálogo mayorista"));
  }, []);

  const setQty = (id, qty) => setCantidades({ ...cantidades, [id]: Math.max(0, qty) });

  if (!catalogo) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-800" />
      </div>
    );
  }

  const items = catalogo.productos
    .filter((p) => (cantidades[p.producto_id] || 0) > 0)
    .map((p) => ({ ...p, cantidad: cantidades[p.producto_id] }));
  const totalUnidades = items.reduce((s, i) => s + i.cantidad, 0);
  const total = items.reduce((s, i) => s + i.cantidad * i.precio_mayorista, 0);
  const margen = items.reduce((s, i) => s + i.cantidad * i.margen_unitario, 0);

  const { minimo_compra_unidades, minimo_compra_valor } = catalogo.config;
  const bajoMinimo =
    (minimo_compra_unidades > 0 && totalUnidades < minimo_compra_unidades) ||
    (minimo_compra_valor > 0 && total < minimo_compra_valor);

  const enviar = async () => {
    setEnviando(true);
    try {
      const res = await api.post("/mayoristas/me/pedidos", {
        items: items.map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad })),
        notas,
        fecha_entrega: fechaEntrega || null,
        requiere_factura: requiereFactura,
        datos_facturacion: requiereFactura ? fact : null,
      });
      toast.success(`Pedido ${res.data.numero} enviado a bodega`);
      navigate("/mayorista/pedidos");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear el pedido");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="mayorista-comprar-page">
      <h2 className="text-lg font-bold text-[#4A2C0A]">Comprar Inventario</h2>

      <div className="space-y-3">
        {catalogo.productos.map((p) => (
          <div key={p.producto_id} className="bg-white rounded-2xl border p-4 flex items-center gap-3">
            <div className="flex-1">
              <p className="font-semibold text-[#4A2C0A]">{p.nombre}</p>
              <p className="text-sm">
                <span className="text-blue-800 font-bold">{fmt(p.precio_mayorista)}</span>{" "}
                <span className="text-gray-400 line-through text-xs">{fmt(p.precio_publico)}</span>
              </p>
              <p className="text-xs text-green-700">Margen: {fmt(p.margen_unitario)}/und</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                className="h-9 w-9 rounded-full"
                onClick={() => setQty(p.producto_id, (cantidades[p.producto_id] || 0) - 1)}
                data-testid={`qty-minus-${p.nombre}`}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <input
                type="number"
                min="0"
                value={cantidades[p.producto_id] || 0}
                onChange={(e) => setQty(p.producto_id, parseInt(e.target.value) || 0)}
                className="w-14 text-center border rounded-lg h-9 font-semibold"
                data-testid={`qty-input-${p.nombre}`}
              />
              <Button
                size="icon"
                className="h-9 w-9 rounded-full bg-blue-800 hover:bg-blue-900"
                onClick={() => setQty(p.producto_id, (cantidades[p.producto_id] || 0) + 1)}
                data-testid={`qty-plus-${p.nombre}`}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border p-4 space-y-2">
        <p className="text-sm font-semibold text-[#4A2C0A]">Fecha de entrega solicitada {catalogo.config.anticipacion_horas ? `(mín. ${catalogo.config.anticipacion_horas}h de anticipación)` : ""}</p>
        <input type="date" value={fechaEntrega} onChange={(e) => setFechaEntrega(e.target.value)}
          className="w-full border rounded-lg h-10 px-3 text-sm" data-testid="pedido-fecha-entrega" />
        <label className="flex items-center gap-2 pt-1 cursor-pointer text-sm text-[#4A2C0A]">
          <input type="checkbox" checked={requiereFactura} onChange={(e) => setRequiereFactura(e.target.checked)} data-testid="pedido-factura-check" />
          Requiero factura electrónica
        </label>
        {requiereFactura && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="pedido-factura-campos">
            {[["tipo_persona", "Tipo de persona (Natural/Jurídica)"], ["tipo_documento", "Tipo de documento (NIT/CC)"],
              ["numero_documento", "Número de documento"], ["razon_social", "Razón social"],
              ["email_facturacion", "Email de facturación"], ["direccion_fiscal", "Dirección fiscal"],
              ["ciudad_fiscal", "Ciudad fiscal"]].map(([k, l]) => (
              <input key={k} placeholder={l} value={fact[k] || ""} onChange={(e) => setFact({ ...fact, [k]: e.target.value })}
                className="border rounded-lg h-9 px-3 text-sm" data-testid={`fact-${k}`} />
            ))}
          </div>
        )}
      </div>

      <Textarea
        placeholder="Observaciones del pedido (opcional)"
        value={notas}
        onChange={(e) => setNotas(e.target.value)}
        className="bg-white"
        data-testid="pedido-notas-input"
      />

      {totalUnidades > 0 && (
        <div className="bg-white rounded-2xl border p-4 space-y-2 sticky bottom-20">
          <div className="flex justify-between text-sm">
            <span>Unidades</span>
            <span className="font-semibold">{totalUnidades}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Margen estimado</span>
            <span className="font-semibold text-green-700">{fmt(margen)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold text-[#4A2C0A]">
            <span>Total a pagar</span>
            <span data-testid="pedido-total">{fmt(total)}</span>
          </div>
          {bajoMinimo && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="minimo-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Compra mínima: {minimo_compra_unidades > 0 && `${minimo_compra_unidades} unidades`}
              {minimo_compra_unidades > 0 && minimo_compra_valor > 0 && " / "}
              {minimo_compra_valor > 0 && fmt(minimo_compra_valor)}
            </div>
          )}
          <Button
            className="w-full bg-blue-800 hover:bg-blue-900 h-12 rounded-xl"
            onClick={enviar}
            disabled={enviando || bajoMinimo}
            data-testid="enviar-pedido-btn"
          >
            {enviando ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ShoppingCart className="w-5 h-5 mr-2" />}
            Enviar Pedido a Bodega
          </Button>
        </div>
      )}
    </div>
  );
}
